/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { cloneDeep, get, isEmpty, isString, pick, omit } from 'lodash';

// Components
import EventDetails from 'shared/ui/components/registrations/EventDetails.vue';
import FormFields from 'shared/ui/components/registrations/FormFields.vue';
import Design from 'shared/ui/components/registrations/Design.vue';
import Emails from 'shared/ui/components/registrations/Emails.vue';
import Settings from 'shared/ui/components/registrations/Settings.vue';
import SecondaryToolbar from 'shared/ui/components/form-components/SecondaryToolbar.vue';

// Constants
const COMPONENT_SCOPE_MAP = {
    EventDetails: 'event_details',
    FormFields: 'forms',
    Design: 'theme',
    Emails: 'emails',
    Settings: 'settings'
};

const TARGETED_EMAIL_PROPS = [
    'email_address_confirmation_email',
    'existing_account_confirmation_email',
    'manual_approve_new_reg_email',
    'rsvp_accepted_email',
    'rsvp_declined_email',
    'account_updated_email',
    'access_code_email',
];

const EMAIL_PROPS = [
    ...TARGETED_EMAIL_PROPS,
    'no_account_email'
];

const IMAGE_DOES_NOT_FIT_RATIO = 'image_does_not_fit_ratio';

/**
 * @typedef {object} Asset
 * @property {string} assetName
 * @property {File} [file]
 * @property {boolean} [fitsRatio]
 */

const __vue_script__ = {
    name: 'Configuration',

    components: { EventDetails, FormFields, Design, Emails, Settings, SecondaryToolbar },

    props: {
        event: {
            type: Object,
            required: true
        },

        config: {
            type: Object,
            required: true
        },

        options: {
            type: Object,
            default: () => ({})
        },
    },

    data() {
        return {
            currentComponent: 'EventDetails',
            subpages: ['EventDetails', 'FormFields', 'Design', 'Emails', 'Settings'],
            save: 0,
            pathName: this.config.event_details.path_name,
            submitting: false,
            value: null,
            assetChanged: [],
            loading: false
        };
    },

    computed: {
        webappHref() {
            return this.getWebappHref(true);
        },

        /** @returns {string} */
        urlPreview() {
            return this.getWebappHref();
        },

        /** @returns {string} */
        componentContext() {
            return COMPONENT_SCOPE_MAP[this.currentComponent];
        },

        hasSessionsInForms() {
            return Object.values(this.config.forms || {})
                .some(({ fields }) =>
                    (fields || []).some(field => field.type === 'sessions')
                );
        },

        innerValue: {
            /** @returns {object} */
            get() {
                return this.value[this.componentContext];
            },

            /** @param {object} value */
            set(value) {
                this.value[this.componentContext] = value;
                console.info('[Configuration] New values received', this.componentContext, this.value[this.componentContext]);
            }
        }
    },

    watch: {
        config: {
            deep: true,
            immediate: true,
            handler() {
                this.loading = true;
                this.$nextTick(() => {
                    const val = cloneDeep(this.config);
                    this.reformatEmailsSettings(val.settings);
                    val.emails = pick(val.settings, EMAIL_PROPS);
                    val.settings = omit(val.settings, EMAIL_PROPS);
                    this.value = val;
                    this.loading = false;
                });
            }
        }
    },

    methods: {
        onAssetChanged(assetName) {
            this.assetChanged.push(assetName);
        },

        /**
         * Asks the child component to validate and if
         * it successfull the component will emit the save event, triggering the
         * `saveChanges` handler.
         */
        submit() {
            this.save = Date.now();
        },

        /**
         * Reformat email configuration from old format to new format.
         * @param {Object} emailsConfig configuration to reformat
         */
        reformatEmailsSettings(emailsConfig) {
            for (let prop of TARGETED_EMAIL_PROPS) {
                const value = emailsConfig[prop];
                if (!value || isString(value)) {
                    emailsConfig[prop] = {
                        default: value,
                        targeted: [],
                    };
                }
            }
        },

        /**
         * Saves every changes occurred in the component
         *
         */
        async saveChanges(updateMetadata) {
            try {
                if (this.submitting) {
                    return;
                }

                this.submitting = true;

                const context = this.componentContext;

                const toDelete = this.assetsToDelete();
                const toUpload = this.assetsToUpload();

                if (toUpload.some(({ fitsRatio }) => !fitsRatio)) {
                    throw IMAGE_DOES_NOT_FIT_RATIO;
                }

                await Promise.all(toDelete.map(d => this.deleteAsset(d)));
                await Promise.all(toUpload.map(d => this.uploadAsset(d)));

                const saveContext = context === 'emails' ? 'settings' : context;
                const data = { [saveContext]: this.innerValue };
                const config = await this.$services.pax.saveRegistrationConfig(this.event._id, data);

                this.$emit('updated', context);

                if (updateMetadata) {
                    await this.$services.metadata.getMetadata(this.event._id, 0);
                }

                // Only refresh this once it has been saved and the config reloaded.
                // This to avoid preview and clipboard copy to have a wrong URL
                this.pathName = config.event_details.path_name;

                this.$notify({
                    group: 'flashes',
                    text: this.$i18n.t('registration.configuration.messages.save_success').toString(),
                    type: 'success'
                });

            } catch (error) {
                console.error('[Configuration] Could not save changes:', error.message);

                const text = error === IMAGE_DOES_NOT_FIT_RATIO
                    ? this.$i18n.t('registration.configuration.messages.image_does_not_fit').toString()
                    : this.$i18n.t('registration.configuration.messages.save_failed').toString();

                this.$notify({
                    group: 'flashes',
                    text,
                    type: 'error'
                });
            } finally {
                this.submitting = false;
            }
        },

        /**
         * Checks if the give asset has changed
         *
         * @param {string} assetName the name of the asset to check
         *
         * @returns {boolean}
         */
        assetsChanged(assetName) {
            return this.assetChanged.includes(assetName);
        },

        /**
         * @returns {string[]}
         */
        getAssetsNamesFromConfig() {
            return Object.keys(this.innerValue)
                .map(field => {
                    if (field.startsWith('has_') && field.endsWith('_asset')) {
                        return field.replace('has_', '').replace('_asset', '');
                    }
                    return;
                })
                .filter(a => a);
        },

        /**
         * Checks assets that need to be deleted
         *
         * @returns {{ assetName: string }[]}
         */
        assetsToDelete() {
            const toDelete = [];
            const assets = this.getAssetsNamesFromConfig();
            for (const asset of assets) {
                if (this.assetsChanged(asset) && isEmpty(this.innerValue[asset])) {
                    toDelete.push(asset);
                }
            }

            return toDelete.map(assetName => ({ assetName }));
        },

        /**
         * Checks if an asset needs to be updated
         *
         * unchanged images have file but its size is 0
         *
         * @returns {Asset[]}
         */
        assetsToUpload() {
            const toUpload = [];
            const assets = this.getAssetsNamesFromConfig();
            for (const assetName of assets) {
                const file = get(this.innerValue[assetName], '0.file');
                const fitsRatio = get(this.innerValue[assetName], '0.fitsRatio');
                if (this.assetsChanged(assetName) && file && file.type) {
                    toUpload.push({ assetName, file, fitsRatio });
                }
            }

            return toUpload;
        },

        /**
         * Uploads an asset
         *
         * @param {Asset} asset
         */
        async uploadAsset({ assetName, file }) {
            console.debug('[Configuration] Uploading asset', assetName);
            await this.$services.pax.uploadAsset(this.event._id, file, assetName);
            this.assetChanged = this.assetChanged.filter(a => a !== assetName);
        },

        /**
         * Deletes an asset
         *
         * @param {Asset} asset
         */
        async deleteAsset({ assetName }) {
            console.debug('[Configuration] Deleting asset', assetName);
            try {
                await this.$services.pax.deleteAsset(this.event._id, assetName);
            } catch (err) {
                if (err.response?.status !== 404) {
                    throw err;
                }
            }
            this.assetChanged = this.assetChanged.filter(a => a !== assetName);
        },

        /**
         * Copies the preview URL into the clipboard
         */
        async copyLink() {
            await this.$copyText(this.urlPreview);

            this.$notify({
                group: 'flashes',
                text: this.$i18n.t('general.copy_success').toString(),
                type: 'success'
            });
        },

        /**
         * Gets the webapp URL
         *
         * @param {boolean} [noCache] whether to append the cache buster or not
         *
         * @returns {string} the URL string for the webapp
         */
        getWebappHref(noCache) {
            return this.event.is_template
                ? this.$utils.url.getWebappRegistrationUrlForTemplate(this.event._id)
                : this.$utils.url.getWebappRegistrationUrl(this.event.branded_app, this.pathName, noCache, this.event.white_label_domain);
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"registrations-configuration"},[_c('div',{staticClass:"title"},[_c('h1',[_vm._v(_vm._s(_vm.$t('registration.configuration.title')))])]),_c('div',{staticClass:"content"},[_c('div',{staticClass:"nav-list-wrapper"},[_c('div',{staticClass:"repeated-item-list-container"},[_c('ul',{staticClass:"nav nav-list"},_vm._l((_vm.subpages),function(subpage){return _c('li',{key:subpage,class:{active: _vm.currentComponent === subpage}},[_c('a',{staticClass:"has-action",on:{"click":function($event){_vm.currentComponent = subpage}}},[_vm._v(_vm._s(_vm.$t(("registration.configuration." + (_vm.$utils.strings.toSnakeCase(subpage))))))])])}),0)])]),(!_vm.loading)?_c('div',{staticClass:"view-wrapper"},[_c(_vm.currentComponent,{key:_vm.currentComponent,tag:"component",attrs:{"save-spy":_vm.save,"event":_vm.event,"settings":_vm.config.settings,"event-details":_vm.config.event_details,"options":_vm.options,"has-sessions-in-forms":_vm.hasSessionsInForms},on:{"save":_vm.saveChanges,"asset-changed":_vm.onAssetChanged},scopedSlots:_vm._u([{key:"manual-approval-link",fn:function(){return [_vm._t("manual-approval-link")]},proxy:true}],null,true),model:{value:(_vm.innerValue),callback:function ($$v) {_vm.innerValue=$$v},expression:"innerValue"}})],1):_vm._e()]),_c('secondary-toolbar',[_c('div',{staticClass:"contextual"},[(_vm.urlPreview)?_c('a',{staticClass:"btn btn-icon",attrs:{"href":_vm.urlPreview,"target":"_blank","rel":"noopener noreferrer"},on:{"click":function($event){$event.stopPropagation();$event.preventDefault();return _vm.copyLink()}}},[_c('i',{staticClass:"icon-link"}),_vm._v(_vm._s(_vm.$t('registration.configuration.copy_link')))]):_vm._e(),(_vm.urlPreview)?_c('a',{staticClass:"btn btn-icon",attrs:{"href":_vm.webappHref,"target":"_blank","rel":"noopener noreferrer"}},[_c('i',{staticClass:"ic-open"}),_vm._v(_vm._s(_vm.$t('registration.configuration.preview_page')))]):_vm._e()]),_c('button',{staticClass:"btn btn-primary",attrs:{"disabled":_vm.submitting},on:{"click":_vm.submit}},[_vm._v(_vm._s(_vm.$t('general.save')))])])],1)}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "Configuration.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__